import { Component, OnInit, Input, inject, DestroyRef } from '@angular/core';
import { Constants } from 'src/app/models/constants';
import { SharedService } from '../../../services/shared/shared.service';
import { DatePipe } from '@angular/common';
import * as moment_timezone from 'moment-timezone';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { FormBuilder, FormGroup } from '@angular/forms';
import { StorageService } from 'src/app/services/storage.service';
import { DateTimeService } from 'src/app/services/date-time/date-time.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';
import { Router } from '@angular/router';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const dateReportFormat = "YYYYMMDDHHmmssS";
const dateReportFormatIT = "YYYYMMDDHHmmss";
const momentTz = moment_timezone;

@Component({
  selector: 'download-data',
  standalone: false,
  templateUrl: './download.component.html',
  styleUrls: ['./download.component.scss']
})
export class DownloadComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  
  public handleTabfocusAction = new Constants();

  @Input() displayDownloadIcon: boolean = false;
  @Input() fileName: string;
  @Input() baseStorage: string;
  @Input() fileStore: boolean = false;
  @Input() blobStore: boolean = false;
  @Input() container: string = '';
  @Input() path: string;
  @Input() btnName: string = "Download";
  @Input() type: string = "button";
  @Input() fileType: string;
  @Input() fileId: string;
  @Input() count: number;
  @Input() date: Date;
  @Input() downloadDate: Date;
  @Input() source: string;
  @Input() mofInterface: string;
  @Input() transform: string;
  @Input() bypassCheck: boolean = false;
  @Input() fileStandard: string = "EY";
  @Input() reportCateg: string = "fileId";
  @Input() fileExt: string;
  @Input() orgFileName: string;
  @Input() fileStatus: string;
  @Input() fileTemplate: string;
  @Input() errorMsg: string;
  @Input() isPopupApplicable: boolean = false;
  @Input() dbFileStatus: string;
  @Input() gvrtStatus: string;
  @Input() disableClass: any = false;

  valid: boolean = false;
  displayInvalidDialog: boolean = false;
  validationMessage: string = "";
  lang: string;
  public splitDownloadItems: MenuItem[];

  public selectedCountry: string;

  constructor(
    private sharedService: SharedService,
    public datepipe: DatePipe,
    private translate: TranslateService,
    private formBuilder: FormBuilder,
    private storageService: StorageService,
    private dateTimeService: DateTimeService,
    private router: Router) {

  }

  ngOnInit() {
    this.selectedCountry = this.storageService.getWithDefaultPassphrase("Country");

    if (this.count > 0 || this.bypassCheck) {
      this.valid = true;
    }

    this.translate.onLangChange
      .pipe(
        startWith({ lang: this.translate.currentLang || this.translate.getDefaultLang() } as LangChangeEvent),
        takeUntilDestroyed(this.destroyRef))
      .subscribe((event: LangChangeEvent) => {
        this.lang = event.lang;
        this.updateDownloadOptions();
      });
  }

  downloadFile(fileName?: string): void {
    this.fileName = !!fileName ? `${fileName}.xlsx` : this.fileName;
    this.sharedService.downloadFile(this.fileName, this.baseStorage, this.fileStore, this.blobStore, this.container, this.path)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        if (response) {
          this.saveToFileSystem(response, 'application/zip', this.getTemplateName(fileName));
        }
      });
  }

  getTemplateName(fileName: string): string {
    let templateName: string;
    switch (fileName) {
      case "EInvoice": {
        templateName = 'EInvoice.xlsx';
        break;
      }
      case "Delete": {
        templateName = 'Delete Data.xlsx';
        break;
      }
      default: {
        templateName = this.fileName;
        break;
      }
    }

    return templateName;
  }

  // TODO: update this method to avoid potential race condition by this.checkFileExist() and this.checkRecordsDumped() for example
  checkReportCateg(event: Event): void {
    if (this.disableClass) {
      event.preventDefault();
    } else {
      if (this.reportCateg === 'fileId') {
        let path = this.datepipe.transform(this.downloadDate, 'yyyyMMdd');
        if (this.fileType === "EY_TRANSFORMATION") {
          this.checkFileExist(path);
        } else if (this.fileType === "EY_ACTIVE_FR") {
          // this.downloadXmlZipFile(path); // to do when required for FR 
        }
        if (this.isPopupApplicable) {
          if (this.dbFileStatus === "PROCESSED" && this.errorMsg && this.errorMsg !== "" && this.errorMsg !== "undefined" && this.errorMsg !== "null") {
            this.checkRecordsDumped(path);
          } else {
            this.downloadReportForFileIDForm(path);
          }
        } else if (this.fileType !== "EY_TRANSFORMATION") {
          this.downloadReportForFileIDForm(path);
        }
      } else {
        this.downloadReportForRequestsForm(this.reportCateg);
      }
    }
  }

  checkFileExist(path: string): void {
    this.sharedService.checkTransformationFile(this.fileId, this.fileType, path, this.fileName, this.fileStatus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(resp => {
        if (resp) {
          this.downloadReportForFileIDForm(path);
        } else {
          this.translate.get('text.downloadValidation').subscribe(res => {
            this.validationMessage = res;
            this.displayInvalidDialog = true;
          });
        }
      });
  }

  checkRecordsDumped(path: string): void {
    let isEyReport = this.fileType.includes("EY_");
    this.sharedService.checkRecordsDumpedForPopup(this.fileId, this.fileType, isEyReport)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(resp => {
        if (resp) {
          this.downloadReportForFileIDForm(path);
        } else {
          if (this.fileType.includes("EY_ACTIVE_ERRORS_")) {
            this.validationMessage = "Cannot provide the error file as the data in the staging file is not provided properly.";
          } else if (!this.fileType.includes("EY_ACTIVE_ERRORS_")) {
            let downloadRptType = this.fileType.includes("ERROR") ? "Error" : "Processed";
            this.validationMessage = "Cannot provide the " + downloadRptType + " file as the data in the staging file is not provided properly.";
          }
          this.displayInvalidDialog = true;
        }
      });
  }

  downloadReportForFileIDForm(path: string): void {
    const userTime = this.selectedCountry === 'MY' ? `${this.dateTimeService.getUserCurrentDateTime()}_${this.dateTimeService.getTimeZone()}` : "";
    const userTimeZone = this.selectedCountry === 'MY' ? this.dateTimeService.getTimeZone() : "";
    let isEyReport = this.fileType.includes("EY_");
    let isGVRT = this.fileType.includes("GVRT_");
    let orgNameWoExt = this.orgFileName.substr(0, this.orgFileName.lastIndexOf('.'));
    let extension = '';
    extension = this.fileExt === "default" ? this.fileName.substr(this.fileName.lastIndexOf('.') + 1) : this.fileExt;
    if(this.selectedCountry == "PL" && this.fileTemplate === "Standard Invoice - Attachment" && this.router.url?.includes('data-status')) {
    extension = "zip";
    }
    else if(this.selectedCountry == "FR" && this.fileTemplate === "Standard Invoice - Attachment" && this.router.url?.includes('data-status') && this.fileType == "EY_TOTAL") {
    extension = "zip";
    }
    // if(this.selectedCountry == "FR"){
    //  extension =  this.fileName.substr(this.fileName.lastIndexOf('.') + 1)
    // }
    let fileTemplate = this.fileTemplate === "Delete" ? "CAN" : "SALE";

    if (isGVRT) {
      this.sharedService.downloadGVRTFile(this.fileId, this.gvrtStatus)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(response => {
          if (response) {
            let successOrError = this.gvrtStatus.toUpperCase() === "SENT" ? "_Success_" : "_Error_";
            let fileName = "GVRT_" + orgNameWoExt + "_1" + successOrError + momentTz.tz("EET").format(dateReportFormat) + "." + extension;
            this.saveToFileSystem(response, this.getBlobType(extension), fileName);
          }
        });
    } else if (!isEyReport) {
      let downloadFrom = this.formBuilder.group({
        fileId: this.fileId,
        type: this.fileType,
        path: path,
        fileName: this.fileName,
        orgFileName: this.orgFileName,
        source: this.source,
        mofInterface: this.mofInterface,
        dataTransformationType: this.transform === undefined ? "No Transformation": this.transform,
        extension: extension,
        fileTemplate: fileTemplate,
        userTime: userTime
      });
      this.sharedService.downloadMofReportForm(downloadFrom);
    } else {
      let downloadFrom = this.formBuilder.group({
        fileId: this.fileId,
        type: this.fileType,
        path: path,
        fileName: this.fileName,
        orgFileName: this.orgFileName,
        source: this.source,
        mofInterface: this.mofInterface,
        dataTransformationType: this.transform === undefined ? "No Transformation": this.transform,
        fileStandard: this.fileStandard,
        extension: extension,
        fileStatus: this.fileStatus,
        fileTemplate: fileTemplate,
        userTime: userTime,
        userTimeZone: userTimeZone
      });
      this.sharedService.downloadEYReportForm(downloadFrom);
    }
  }

  private getBlobType(extension: string): string {
    let type: string;
    switch (extension) {
      case "xlsx": {
        type = EXCEL_TYPE;
        break;
      }
      case "csv": {
        type = 'text/csv';
        break;
      }
      case "zip": {
        type = 'application/zip';
        break;
      }
      case "txt": {
        type = 'text/plain';
        break;
      }
    }

    return type;
  }

  private saveToFileSystem(response, type: string, fileName): void {
    const blob = new Blob([response.body], {
      type: type
    });

    if (blob.size > 0) {
      const a: any = document.createElement('a');
      document.body.appendChild(a);
      a.style = 'display: none';
      const url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      this.translate.get('text.downloadValidation').subscribe(res => {
        this.validationMessage = res; // "There is no data to download"
        this.displayInvalidDialog = true;
      });
    }
  }

  closeErrorDialog(): void {
    this.displayInvalidDialog = false;
    this.validationMessage = "";
  }

  getFileName(orgNameWoExt: string): string {
    let timestamp = this.fileName.split('_')[0];

    if (this.fileType === "EY_TOTAL" && this.fileStandard === "EY") {
      return orgNameWoExt + "_TotalReceived_" + this.source + "_DataStatus_" + timestamp;
    }
    else if (this.fileType === "EY_TOTAL" && this.fileStandard !== "EY") {
      return orgNameWoExt + "_TotalReceived_" + this.transform + "_" + this.source + "_DataStatus_" + timestamp;
    }
    else if (this.fileType === "EY_TRANSFORMATION" && this.fileStatus === "Transformation Failed") {
      return orgNameWoExt + "_TransformationError_" + this.transform + "_" + this.source + "_DataStatus_" + timestamp;
    }
    else if (this.fileType === "EY_TRANSFORMATION" && this.fileStatus !== "Transformation Failed") {
      return orgNameWoExt + "_TransformationSuccess_" + this.transform + "_" + this.source + "_DataStatus_" + timestamp;
    }
    else if (this.fileType === "EY_ACTIVE_ERRORS") {
      return orgNameWoExt + "_EYActiveError_" + this.source + "_DataStatus_" + timestamp;
    }
    else if (this.fileType === "EY_ACTIVE") {
      return orgNameWoExt + "_EYActiveSuccess_" + this.source + "_DataStatus_" + timestamp;
    }
    else if (this.fileType === "EY_ACTIVE") {
      return orgNameWoExt + "_EYActiveSuccess_" + this.source + "_DataStatus_" + timestamp;
    }
    else if (this.fileType === "MOF_SUCCESS") {
      return orgNameWoExt + "_MoFSuccess_" + this.source + "_DataStatus_" + timestamp;
    }
    else if (this.fileType === "MOF_ERROR") {
      return orgNameWoExt + "_MoFError_" + this.source + "_DataStatus_" + timestamp;
    }
    else {
      return orgNameWoExt + "_" + this.source + "_DataStatus_" + timestamp;
    }
  }

  downloadReportForRequests(reportName: string): void {
    this.sharedService.downloadGeneratedReport(this.fileName, this.path)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        if (response) {
          let finalName: string;
          let dateFormat = this.selectedCountry === 'IT' ? dateReportFormatIT : dateReportFormat;
          let timeZone = this.selectedCountry === 'RO' ? 'EET' : 'CET'
          if (reportName == Constants.report || reportName == Constants.xml || reportName == Constants.pdf
            || reportName == Constants.OUT_SDI || reportName == Constants.OUT_RESP_SDI) {
            finalName = this.fetchReportPrefix(reportName) + "_InvoiceMgmt_".concat(this.fileName) + "_" + momentTz.tz(timeZone).format(dateFormat) + ".zip";
          } else if (reportName == Constants.IN_SDI) {
            finalName = this.fetchReportPrefix(reportName) + "_InwardInvoices_".concat(this.fileName) + "_" + momentTz.tz(timeZone).format(dateFormat) + ".zip";
          } else {
            finalName = (this.selectedCountry === 'RO' || this.selectedCountry === 'IT') && this.path.includes("Fetch")
              ? "InwardInvoices_".concat(this.fileName).concat("_").concat(this.datepipe.transform(this.downloadDate,
                this.timeStampFormat(this.selectedCountry, "IN_XML"))).concat(".zip")
              : momentTz.tz(timeZone).format(dateReportFormat) + "_".concat(this.fileName) + ".zip";
          }
          this.saveToFileSystem(response, 'application/zip', finalName);
        }
      });
  }

  downloadReportForRequestsForm(reportName: string): void {
    let downloadFrom: FormGroup = this.formBuilder.group({
      reportName: reportName,
      category: this.path,
      requestId: this.fileName,
      timeZone: this.selectedCountry === 'MY' ? this.dateTimeService.getTimeZone() : null,
    });
    // if (this.selectedCountry == 'MY') {
    //   downloadFrom.addControl('timeZone', this.dateTimeService.getTimeZone());
    // }
    this.sharedService.downloadGeneratedReportForm(downloadFrom)
  }

  fetchReportPrefix(reportName: string): string {
    let rptPrefix = reportName;
    switch (reportName) {
      case Constants.OUT_SDI:
        rptPrefix = "GTES_SDI_XML";
        break;
      case Constants.OUT_RESP_SDI:
        rptPrefix = "SDI_Response_XML";
        break;
      case Constants.IN_SDI:
        rptPrefix = "SDI_XML";
        break;
    }
    return rptPrefix;
  }

  timeStampFormat(country: string, reportType: string): string {
    let format: string;
    switch (country + "_" + reportType) {
      case "RO_IN_XML": {
        format = "YYYYMMddHHmmssS";
        break;
      }
      case "IT_IN_XML": {
        format = "YYYYMMddHHmmss";
        break;
      }
    }
    return format;
  }

  downloadZipFile(path: string): void {
    let downloadFrom = this.formBuilder.group({
      fileId: this.fileId,
      type: this.fileType,
      path: path,
      fileName: this.fileName,
      orgFileName: this.orgFileName,
      extension: 'zip'
    });
    this.sharedService.downloadMofZipReportForm(downloadFrom);
  }

  downloadXmlZipFile(path: string): void {
    let downloadFrom = this.formBuilder.group({
      fileId: this.fileId,
      type: this.fileType,
      path: path,
      fileName: this.fileName,
      orgFileName: this.orgFileName,
      extension: 'zip'
    });
    this.sharedService.downloadEyXmlReportForm(downloadFrom);
  }

  private updateDownloadOptions(): void {
    const translationKeys = [
      'text.EInvoice',
      'text.attachmentdata',
      'text.EInvoiceRadioD',
      'text.eReportingTemplate',
      'text.lifeCycle'
    ];
    this.translate.get(translationKeys).subscribe(res => {
      if (this.selectedCountry === 'FR') {
        this.splitDownloadItems = [
          {
            items: [
              {
                label: res['text.EInvoice'],
                icon: '',
                command: () => {
                  this.downloadFile('EInvoice');
                }
              },
              {
                label: res['text.eReportingTemplate'],
                icon: '',
                command: () => {
                  this.downloadFile('EReporting');
                }
              },
              {
                label: res['text.lifeCycle'],
                icon: '',
                command: () => {
                  this.downloadFile('LifeCycle');
                }
              }
            ]
          }
        ];
        return;
      }
      this.splitDownloadItems = [
        {
          items: [
            {
              label: res['text.EInvoice'],
              icon: '',
              command: () => {
                this.downloadFile('EInvoice');
              }
            },
            // Only show AttachmentData if selectedCountry is PL
            ...(this.selectedCountry === 'PL' ? [{
              label: res['text.attachmentdata'],
              icon: '',
              command: () => {
                this.downloadFile('Attachment Data');
              }
            }] : []),
            {
              label: res['text.EInvoiceRadioD'],
              icon: '',
              command: () => {
                this.downloadFile('Delete');
              }
            }
          ]
        }
      ];
    });
  }

}
